import React, { memo, useMemo, Fragment, useState, useCallback } from 'react';
import { BlockAdvanceFormData } from '../Common/AdvanceSettings';
import { R } from '@core/utils/r';
import { GoogleFontLoader } from '@modules/common/GoogleFontLoader';
import { Collapse, Container } from 'reactstrap';
import { useProposalBlockContext, useProposalCollapse, useProposalContext, useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { useObservable } from '@core/utils/hooks/rxjs';
import { FormHTMLInline } from '@core/components/form';
import { findTitleFormat, titleFormatStyle } from './titleFormat';
import './index.scss';

export interface BlockContainerProps extends React.HTMLProps<HTMLDivElement> {
  advanceForm?: BlockAdvanceFormData;
  excludeStyles?: (keyof BlockAdvanceFormData)[];
  containerStyles?: React.CSSProperties;
  isSection?: boolean;
  sectionTitle?: string;
  fullWidth?: boolean;
  noStyle?: boolean;
  editable?: boolean;
}
export const BlockContainer = memo((props: BlockContainerProps) => {
  const {
    advanceForm,
    style = {},
    excludeStyles,
    isSection = false,
    sectionTitle,
    containerStyles,
    fullWidth = true,
    noStyle = false,
    ...rest
  } = props;

  const data = useProposalContext();
  const mainFormValue = useObservable(data?.form.value$);
  const blocBase = useProposalBlockContext();
  const blockForm = blocBase.blockForm;

  const carouselSize = useMemo(() => {
    const size = mainFormValue?.carouselSectionDropDownSize;
    if (typeof size === 'string' && size) {
      const parsed = parseInt(size, 10);
      return isNaN(parsed) ? 24 : parsed;
    }
    return typeof size === 'number' ? size : 24;
  }, [mainFormValue?.carouselSectionDropDownSize]);

  const carouselColor = mainFormValue?.carouselSectionColor || '#000000';

  const fontFamily = useMemo(() => {
    if (advanceForm?.font?.family) {
      return advanceForm.font.family;
    }
    return undefined;
  }, [advanceForm]);

  // Builder keeps sections expanded for editing; the client-facing (read-only) proposal shows
  // condensed, collapsed categories that the client expands on demand.
  const [localOpen, setLocalOpen] = useState(!!props.editable);
  // Inside ProposalBlocs the section's open state is the page collapse tree's (collapsing it also hides every block
  // nested under it); elsewhere (widget previews) it stays local.
  const collapse = useProposalCollapse();
  const blockIndex = blocBase?.blockIndex;
  const isOpen = collapse && blockIndex !== undefined ? !collapse.isCollapsed(blockIndex) : localOpen;
  const setOpen = useCallback(
    (open: boolean) => (collapse && blockIndex !== undefined ? collapse.toggle(blockIndex, !open) : setLocalOpen(open)),
    [collapse, blockIndex],
  );
  const blockFormValue = useObservable(blockForm.value$);

  const content = useMemo(() => {
    if (!isSection) {
      return props.children;
    } else {
      const description = blockFormValue?.description;
      const image = blockFormValue?.image;
      // LEFT: description inline right after the title. CENTER/RIGHT: description below the
      // title, aligned to match. The image always sits right before the title.
      // A saved title format (Block Title 1/2/3, template settings) supplies the styling AND the position.
      const format = findTitleFormat(mainFormValue?.blockTitleFormats, blockFormValue?.titleFormat);
      const formatted = titleFormatStyle(format);
      const position = format?.position || blockFormValue?.titlePosition || 'left';
      const justify = position === 'center' ? 'justify-content-center' : position === 'right' ? 'justify-content-end' : '';

      return (
        <Fragment>
          <div
            className="action d-flex align-items-center"
            // Read-only (client) view: the whole header is the expand/collapse target.
            // Builder: only the chevron toggles, so clicking the title still edits it inline.
            onClick={props.editable ? undefined : () => setOpen(!isOpen)}
            style={props.editable ? undefined : { cursor: 'pointer' }}
          >
            <a
              className="ml-2"
              onClick={e => {
                e.stopPropagation();
                setOpen(!isOpen);
              }}
              style={{ cursor: 'pointer', color: carouselColor, fontSize: `${carouselSize}px`, flexShrink: 0 }}
            >
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: `${carouselSize}px`, color: carouselColor }}></i>
            </a>
            {/* Condensed header content only shows while COLLAPSED (expanded = chevron only,
                like the original behavior — the section's own content carries its heading) */}
            {!isOpen && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={`d-flex align-items-center flex-wrap ${justify}`}>
                  {!R.isEmpty(image) && (
                    <img
                      src={image}
                      alt=""
                      style={{
                        width: 72,
                        height: 72,
                        objectFit: 'cover',
                        marginLeft: 8,
                        marginRight: 14,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {/* FormHTMLInline's root is flex:1 — wrap it so the title shrinks to its content
                      and the description sits right after it instead of at the far edge */}
                  {!!format?.font && <GoogleFontLoader font={format.font} />}
                  <div className={`d-flex ${formatted.className}`} style={{ flex: '0 1 auto', minWidth: 0, ...formatted.style }}>
                    <FormHTMLInline control={blockForm.controls.title} readonly={!props.editable} placeholder="Section Title" />
                  </div>
                  {/* Description is inline-editable HTML like the title (editable in the builder
                      while collapsed; hidden in read-only views when empty) */}
                  {position === 'left' && (props.editable || !R.isEmpty(description)) && (
                    <div className="d-flex ml-3" style={{ opacity: 0.7, fontSize: '0.65em', lineHeight: 1.4, flex: '0 1 auto', minWidth: 0 }}>
                      <FormHTMLInline control={blockForm.controls.description} readonly={!props.editable} placeholder="Short description" />
                    </div>
                  )}
                </div>
                {position !== 'left' && (props.editable || !R.isEmpty(description)) && (
                  <div
                    className={position === 'center' ? 'text-center' : 'text-right'}
                    style={{ opacity: 0.7, fontSize: '0.65em', lineHeight: 1.4 }}
                  >
                    <FormHTMLInline control={blockForm.controls.description} readonly={!props.editable} placeholder="Short description" />
                  </div>
                )}
              </div>
            )}
          </div>
          <Collapse isOpen={isOpen} className="section-block">
            {props.children}
          </Collapse>
        </Fragment>
      );
    }
  }, [isOpen, setOpen, isSection, props.children, sectionTitle, carouselSize, carouselColor, props.editable, blockForm.controls.title, blockForm.controls.description, blockFormValue?.description, blockFormValue?.image, blockFormValue?.titlePosition, blockFormValue?.titleFormat, mainFormValue?.blockTitleFormats]);

  return (
    <Fragment>
      {!!advanceForm?.font && <GoogleFontLoader font={advanceForm?.font} />}
      <div {...rest} style={noStyle ? {} : { ...style, ...R.objectCompact(R.omit(R.omit(advanceForm, excludeStyles || []), 'font')), fontFamily }}>
        {!fullWidth ? <Container style={containerStyles || {}}>{content}</Container> : content}
      </div>
    </Fragment>
  );
});
